import GriffinDate from 'griffin-paypal/date';
import GriffinPhone from 'griffin-paypal/phone';
import GriffinPlural from 'griffin-paypal/plural';

import Page from './page';
import { getMetadata } from './griffin-metadata';

function toLocaleDateStringSupportsLocales() {
  try {
    new Date().toLocaleDateString('i');
  } catch (e) {
    return e.name === 'RangeError';
  }
  return false;
}

function getDateString(date) {
  const metaData = getMetadata();
  try {
    let formattedDate;
    if (metaData) {
      const griffinDateHandler = GriffinDate.createHandler(metaData);
      formattedDate = griffinDateHandler.formatDate(date, {
        style: griffinDateHandler.DATE_FORMAT_SHORT,
        timeZone: 'Etc/UTC',
      });
    } else {
      formattedDate = date.toLocaleDateString(Page.getLocale());
    }
    return formattedDate;
  } catch (e) {
    return undefined;
  }
}

export function getFormattedPhoneNumber(code, phone) {
  const metadata = getMetadata();
  try {
    let formattedPhone;
    if (metadata) {
      const griffinPhoneHandler = GriffinPhone.createHandler(metadata);
      const phoneObj = { countryCode: code, nationalNumber: phone };
      formattedPhone = griffinPhoneHandler.formatPhoneNumber(phoneObj);
    } else {
      return phone;
    }
    return formattedPhone;
  } catch (e) {
    return phone;
  }
}

export function formatAsTyped(phone) {
  const metadata = getMetadata();
  try {
    let formattedPhone;
    if (metadata) {
      const griffinPhoneHandler = GriffinPhone.createHandler(metadata);
      // const phoneObj = { countryCode: code, nationalNumber: phone };
      formattedPhone = griffinPhoneHandler.formatAsTyped(phone, { country: Page.getCountry().toUpperCase() });
    } else {
      return phone;
    }
    return formattedPhone;
  } catch (e) {
    return phone;
  }
}

export function validatePhoneNumber(code, phone) {
  const metadata = getMetadata();
  try {
    let response;
    if (metadata) {
      const griffinPhoneHandler = GriffinPhone.createHandler(metadata);
      const phoneObj = { countryCode: code, nationalNumber: phone };
      response = griffinPhoneHandler.validatePhoneNumber(phoneObj);
      return response;
    }
    return true;
  } catch (e) {
    return true;
  }
}

export function getPlural(ewt) {
  const metadata = getMetadata();
  try {
    let response;
    if (metadata) {
      const griffinPhoneHandler = GriffinPlural.createHandler(metadata);
      response = griffinPhoneHandler.getPlural(ewt);
      return response;
    }
    return 'other';
  } catch (e) {
    return 'other';
  }
}

export const getFormattedDate = (year, month, day) => {
  let result;
  try {
    const monthNo = (month && month > 0) ? month - 1 : 0;
    const createdDate = new Date(Date.UTC(year, monthNo, day));
    if (toLocaleDateStringSupportsLocales()) {
      result = getDateString(createdDate);
    } else {
      result = createdDate.toLocaleDateString();
    }
  } catch (e) {
    result = `${year}-${month}-${day}`;
  }
  return result;
};

const getDateName = (date, style) => {
  const metaData = getMetadata();
  try {
    let formattedDate;
    if (metaData) {
      const griffinDateHandler = GriffinDate.createHandler(metaData);
      formattedDate = griffinDateHandler.formatDate(date, {
        style,
        timeZone: 'Etc/UTC',
      });
    } else {
      formattedDate = date.toLocaleDateString(Page.getLocale());
    }
    return formattedDate;
  } catch (e) {
    return undefined;
  }
};

const getDataMaskingDay = (date, griffinDateHandler, normalizedDate) => {
  const dateInput = griffinDateHandler.dateInputMask({
    style: griffinDateHandler.DATE_FORMAT_SHORT,
    parts: true,
  });

  let index = 0;
  let start = 0;
  let end = 0;
  const maskingDate = {
    day: normalizedDate.getDate(),
    month: normalizedDate.getMonth(),
    year: normalizedDate.getFullYear(),
  };

  for (let i = 0; i < dateInput.length; i += 1) {
    const temp = dateInput[i];
    const tmpLength = temp.value && temp.value.length;

    if (temp.type in maskingDate) {
      start = index;
      end = start + tmpLength;
      const tempDay = date.substring(start, end);
      if (tempDay && maskingDate[temp.type] !== tempDay) {
        maskingDate[temp.type] = tempDay;
      }
    }
    index += tmpLength;
  }

  return maskingDate;
};

export const getNormalizedDate = (date) => {
  const metaData = getMetadata();
  try {
    if (metaData) {
      const griffinDateHandler = GriffinDate.createHandler(metaData);

      const normalizedDate = griffinDateHandler.normalizeDate(date, {
        style: griffinDateHandler.DATE_FORMAT_SHORT,
        timeZone: 'Etc/UTC',
      });

      const { day, month, year } = getDataMaskingDay(date, griffinDateHandler, normalizedDate);

      if (normalizedDate instanceof Date) {
        normalizedDate.setDate(day);
        normalizedDate.setMonth(Number(month) - 1);
        normalizedDate.setFullYear(year);

        return normalizedDate;
      }
      return null;
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const getCurrentDate = () => {
  const now = new Date();

  now.setUTCMilliseconds(0);
  now.setUTCSeconds(0);
  now.setUTCMinutes(0);
  now.getUTCHours(0);

  return now;
};

export const isValidDate = (givenDate) => (givenDate instanceof Date);

const getMonthNames = (format) => {
  const monthNamesList = [];

  for (let monthNo = 0; monthNo < 12; monthNo += 1) {
    monthNamesList.push(getDateName(new Date(Date.UTC(2017, monthNo)), format));
  }

  return monthNamesList;
};

const getDayNames = (format) => {
  const dayNamesList = [];

  for (let dayNo = 1; dayNo < 8; dayNo += 1) {
    dayNamesList.push(getDateName(new Date(Date.UTC(2017, 9, dayNo)), format));
  }

  return dayNamesList;
};

export const getShortMonthNames = () => getMonthNames('MMM');

export const getLongMonthNames = () => getMonthNames('MMMM');

export const getShortDayNames = () => getDayNames('EEE');
